import React, { useState, useEffect, useCallback } from "react";
import { Routes } from "../constant/api_url";
import DataTable from "react-data-table-component";
import {
  Card,
  Stack,
  Container,
  Typography,
  Button,
  Drawer,
  Box,
  IconButton,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  useTheme,
  Tooltip
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Code as CodeIcon,
  ContentCopy,
  CalendarToday,
  Lan,
  Notes,
  Difference,
  ChevronLeft,
  ChevronRight,
  DarkMode,
  LightMode
} from "@mui/icons-material";
import Page from "../components/Page";
import { fetchSystemLogModules, fetchSystemLogs, fetchchapterbyuser } from "src/utils/common";
import ChapterSelectDropDown from "src/components/ChapterSelectDropDown";
import Select from "react-select";

import DatePicker from "@mui/lab/DatePicker";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import moment from "moment";

const ACTION_COLORS = {
  CREATE: { bg: "success.lighter", text: "success.main" },
  UPDATE: { bg: "info.lighter", text: "info.main" },
  DELETE: { bg: "error.lighter", text: "error.main" },
  STATUS_CHANGE: { bg: "warning.lighter", text: "warning.main" }
};

function ActionBadge({ action }) {
  const theme = useTheme();
  const key = ACTION_COLORS[action] || ACTION_COLORS.UPDATE;
  const bg = theme.palette.getContrastText
    ? theme.palette.background.neutral
    : "#F4F6F8";
  const bgColor = theme.palette.background.neutral;
  return (
    <Chip
      label={action}
      size="small"
      sx={{
        backgroundColor: key.bg,
        color: key.text,
        fontWeight: 700,
        fontSize: "11px",
        lineHeight: "12px",
        borderRadius: "4px",
        height: "22px"
      }}
    />
  );
}

// Profile that performed the logged action, derived from system_log.director_chpt_id vs chpt_id.
// Values mirror backend config/constants.js DIRECTOR_TYPE.
const CHANGED_BY_TYPE = { ADDITIONAL_DIRECTOR: 0, CHAPTER_DIRECTOR: 1, MEMBER: 2 };
const CHANGED_BY_TYPE_NAME = {
  [CHANGED_BY_TYPE.ADDITIONAL_DIRECTOR]: "Additional Director",
  [CHANGED_BY_TYPE.CHAPTER_DIRECTOR]: "Chapter Director",
  [CHANGED_BY_TYPE.MEMBER]: "Member"
};
const CHANGED_BY_TYPE_COLOR = {
  [CHANGED_BY_TYPE.ADDITIONAL_DIRECTOR]: "warning",
  [CHANGED_BY_TYPE.CHAPTER_DIRECTOR]: "primary",
  [CHANGED_BY_TYPE.MEMBER]: "default"
};

const getChangedByType = (log) => {
  if (log?.changed_by_type !== undefined && log?.changed_by_type !== null) return Number(log.changed_by_type);
  const directorChptId = log?.director_chpt_id;
  if (directorChptId === undefined || directorChptId === null || String(directorChptId).trim() === "" || String(directorChptId) === "0") {
    return CHANGED_BY_TYPE.MEMBER;
  }
  return String(directorChptId).trim() === String(log?.chpt_id ?? "").trim()
    ? CHANGED_BY_TYPE.CHAPTER_DIRECTOR
    : CHANGED_BY_TYPE.ADDITIONAL_DIRECTOR;
};

const changedByTypeName = (log) => log?.changed_by_type_name || CHANGED_BY_TYPE_NAME[getChangedByType(log)];
const changedByTypeColor = (log) => CHANGED_BY_TYPE_COLOR[getChangedByType(log)];

const MODULE_FIELDS = {
  1: [
    { key: "v2v_id", label: "V2V ID" },
    { key: "m_v2v_status", label: "Status" },
    { key: "comment", label: "Comment" },
    { key: "v2v_img", label: "Image" },
    { key: "v2v_date", label: "Date" },
    { key: "v2v_by_name", label: "V2V By" },
    { key: "v2v_to_name", label: "V2V To" },
    { key: "v2v_approve_by_name", label: "Approved By" },
    { key: "v2v_approved_date", label: "Approved Date" },
    { key: "v2v_reject_reason_name", label: "Reject Reason" },
    { key: "v2v_rejected_date", label: "Rejected Date" }
  ],
  2: [
    { key: "r_id", label: "Reference ID" },
    { key: "r_name", label: "Name" },
    { key: "r_note", label: "Note" },
    { key: "r_type", label: "Type" },
    { key: "r_rating", label: "Rating" },
    { key: "r_mobile_no", label: "Mobile No" },
    { key: "r_date", label: "Date" },
    { key: "r_by_name", label: "Reference By" },
    { key: "r_to_name", label: "Reference To" }
  ],
  3: [
    { key: "b_id", label: "Business ID" },
    { key: "b_amount", label: "Amount" },
    { key: "b_type", label: "Type" },
    { key: "b_thanksnote", label: "Thanks Note" },
    { key: "thanksnote_image", label: "Thanksnote Image" },
    { key: "b_date", label: "Date" },
    { key: "b_by_name", label: "Business By" },
    { key: "b_to_name", label: "Business To" }
  ],
  4: [
    { key: "v_id", label: "Visitor ID" },
    { key: "visitor_name", label: "Name" },
    { key: "visitor_mo_no", label: "Mobile No" },
    { key: "visitor_business_name", label: "Business Name" },
    { key: "visitor_business_category", label: "Business Category" },
    { key: "meeting_id", label: "Meeting ID" },
    { key: "chpt_name", label: "Chapter" },
    { key: "invite_by_name", label: "Invited By" },
    { key: "is_in_chapter", label: "Is In Chapter" }
  ],
  5: [
    { key: "t_id", label: "Testimonial ID" },
    { key: "given_to_name", label: "Given To" },
    { key: "given_by_name", label: "Given By" },
    { key: "comment", label: "Comment" },
    { key: "date", label: "Date" }
  ]
};

const MODULE_NAMES = { 1: "V2V", 2: "Reference", 3: "Business", 4: "Visitor", 5: "Testimonial" };

function ModuleDataCard({ moduleType, oldData, newData }) {
  const theme = useTheme();
  const fields = MODULE_FIELDS[moduleType] || [];
  const moduleName = MODULE_NAMES[moduleType] || `Module ${moduleType}`;
  const data = newData || oldData;

  const IMAGE_KEYS = ["v2v_img", "thanksnote_image"];

  if (!data || fields.length === 0) return null;

  const renderValue = (key, val, label) => {
    if (val === null || val === undefined) {
      return <em style={{ color: theme.palette.text.disabled }}>null</em>;
    }
    if (IMAGE_KEYS.includes(key) && val) {
      const folder = key === "v2v_img" ? "v2v" : "business";
      return (
        <a href={`${Routes.API_HOSTNAME}images/${folder}/${val}`} target="_blank" rel="noopener noreferrer">
          <img
            src={`${Routes.API_HOSTNAME}images/${folder}/${val}`}
            alt={label}
            width="120"
            style={{ borderRadius: 4, border: "1px solid #e0e0e0" }}
          />
        </a>
      );
    }
    return String(val);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="subtitle1"
        fontWeight={600}
        sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
      >
        <CodeIcon sx={{ color: "text.disabled" }} />
        {moduleName} Data
      </Typography>
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden"
        }}
      >
        <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
          <Table size="small">
            <TableBody>
              {fields.map(({ key, label }) => {
                const val = data[key];
                const isDifferent = oldData && newData && JSON.stringify(oldData[key]) !== JSON.stringify(newData[key]);
                return (
                  <TableRow key={key} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        typography: "caption",
                        color: "text.secondary",
                        width: "40%",
                        borderColor: "divider",
                        bgcolor: "background.neutral",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {label}
                    </TableCell>
                    <TableCell
                      sx={{
                        typography: "body2",
                        color: isDifferent ? "warning.dark" : "text.primary",
                        fontWeight: isDifferent ? 600 : 400,
                        borderColor: "divider",
                        bgcolor: isDifferent ? "warning.lighter" : "transparent"
                      }}
                    >
                      {renderValue(key, val, label)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

function JsonTreeView({ data, depth = 0, isDark = false }) {
  const [collapsed, setCollapsed] = useState(false);

  if (data === null) {
    return <Box component="span" sx={{ color: isDark ? "#569CD6" : "#800000", fontWeight: 500 }}>null</Box>;
  }
  if (data === undefined) {
    return <Box component="span" sx={{ color: isDark ? "#569CD6" : "#800000", fontWeight: 500 }}>undefined</Box>;
  }
  if (typeof data === "boolean") {
    return <Box component="span" sx={{ color: isDark ? "#569CD6" : "#800000", fontWeight: 500 }}>{String(data)}</Box>;
  }
  if (typeof data === "number") {
    return <Box component="span" sx={{ color: isDark ? "#B5CEA8" : "#1750EB" }}>{String(data)}</Box>;
  }
  if (typeof data === "string") {
    return (
      <Box component="span" sx={{ color: isDark ? "#CE9178" : "#0A7E0A" }}>
        &quot;{data}&quot;
      </Box>
    );
  }

  const isArray = Array.isArray(data);
  const keys = Object.keys(data);
  const openBrace = isArray ? "[" : "{";
  const closeBrace = isArray ? "]" : "}";
  const itemLabel = isArray ? `items` : `keys`;

  if (keys.length === 0) {
    return (
      <Box component="span" sx={{ color: isDark ? "#D4D4D4" : "text.primary" }}>
        {openBrace}{closeBrace}
      </Box>
    );
  }

  const toggleCollapse = () => setCollapsed(!collapsed);

  return (
    <Box sx={{ fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: "13px", lineHeight: "22px" }}>
      <Box component="span" onClick={toggleCollapse} sx={{ cursor: "pointer", userSelect: "none", color: isDark ? "#D4D4D4" : "text.secondary", mr: 0.5 }}>
        {collapsed ? "\u25B6" : "\u25BC"}
      </Box>
      {collapsed ? (
        <Box component="span" onClick={toggleCollapse} sx={{ cursor: "pointer", color: isDark ? "#D4D4D4" : "text.primary" }}>
          {openBrace} {"..."} {closeBrace}
          <Box component="span" sx={{ color: isDark ? "#858585" : "text.disabled", ml: 1, fontSize: "11px" }}>
            {keys.length} {itemLabel}
          </Box>
        </Box>
      ) : (
        <>
          <Box component="span" sx={{ color: isDark ? "#D4D4D4" : "text.primary" }}>{openBrace}</Box>
          <Box>
            {keys.map((key, idx) => (
              <Box key={key}>
                <Box sx={{ borderLeft: isDark ? "1px dotted #404040" : "1px dotted #e0e0e0", pl: 2 }}>
                  <Box component="span" sx={{ color: isDark ? "#9CDCFE" : "#0451A5" }}>
                    {isArray ? "" : `"${key}"`}
                  </Box>
                  {!isArray && <Box component="span" sx={{ color: isDark ? "#D4D4D4" : "text.primary" }}>: </Box>}
                  <JsonTreeView data={data[key]} depth={depth + 1} isDark={isDark} />
                  {idx < keys.length - 1 && <Box component="span" sx={{ color: isDark ? "#D4D4D4" : "text.primary" }}>,</Box>}
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ color: isDark ? "#D4D4D4" : "text.primary" }}>{closeBrace}</Box>
        </>
      )}
    </Box>
  );
}

function LogDetailPanel({ open, log, onClose }) {
  const theme = useTheme();
  const [showRawJson, setShowRawJson] = useState(false);
  const [jsonDark, setJsonDark] = useState(false);

  if (!log) return null;

  const oldData = log.old_data ? (() => { try { return JSON.parse(log.old_data); } catch { return null; } })() : null;
  const newData = log.new_data ? (() => { try { return JSON.parse(log.new_data); } catch { return null; } })() : null;

  const moduleType = Number(log.module_type);

  const diffFields = oldData && newData
    ? Object.keys(newData).filter((key) => JSON.stringify(oldData[key]) !== JSON.stringify(newData[key]))
    : [];

  const formatValue = (val) => {
    if (val === null || val === undefined) return <em style={{ color: theme.palette.text.disabled }}>null</em>;
    return String(val);
  };

  const hasBothOldNew = oldData && newData;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 600, md: 700 },
          maxWidth: "100%",
          bgcolor: "background.default",
          boxShadow: 24
        }
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(22, 28, 36, 0.6)" } }
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={onClose} size="small" sx={{ color: "text.disabled" }}>
              <CloseIcon />
            </IconButton>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ActionBadge action={log.action_type} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Log Entry Detail
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  mt: 0.25,
                  display: "block"
                }}
              >
                {log.module_name} &gt; {log.source_table}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="caption" color="text.secondary">Record ID:</Typography>
            <Typography variant="body2" fontWeight={600} color="primary.main">
              #{log.record_id}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3 }}>
          {showRawJson ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CodeIcon sx={{ color: "text.disabled" }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Raw JSON Data
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="Toggle dark background">
                      <IconButton size="small" onClick={() => setJsonDark((prev) => !prev)} sx={{ color: "text.disabled" }}>
                        {jsonDark ? <CloseIcon fontSize="small" /> : <CodeIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {hasBothOldNew ? "Old Data" : oldData ? "Old Data (updated)" : "Data"} — values before the change
                </Typography>
              </Box>
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "auto",
                  maxHeight: hasBothOldNew ? "calc(50vh - 240px)" : "calc(100vh - 280px)",
                  bgcolor: jsonDark ? "#1E1E1E" : "background.neutral",
                  position: "relative"
                }}
              >
                <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                  <Tooltip title="Copy JSON">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const raw = oldData ? JSON.stringify(oldData, null, 2) : newData ? JSON.stringify(newData, null, 2) : "";
                        navigator.clipboard.writeText(raw);
                      }}
                      sx={{ color: jsonDark ? "#858585" : "text.disabled", bgcolor: jsonDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)", "&:hover": { bgcolor: jsonDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)" } }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <JsonTreeView data={oldData || newData} isDark={jsonDark} />
                </Box>
              </Box>
              {hasBothOldNew && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                    New Data — values after the change
                  </Typography>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      overflow: "auto",
                      maxHeight: "calc(50vh - 240px)",
                      bgcolor: jsonDark ? "#1E1E1E" : "background.neutral",
                      position: "relative"
                    }}
                  >
                    <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                      <Tooltip title="Copy JSON">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const raw = newData ? JSON.stringify(newData, null, 2) : "";
                            navigator.clipboard.writeText(raw);
                          }}
                          sx={{ color: jsonDark ? "#858585" : "text.disabled", bgcolor: jsonDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)", "&:hover": { bgcolor: jsonDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)" } }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ p: 2.5 }}>
                      <JsonTreeView data={newData} isDark={jsonDark} />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  gap: 3,
                  mb: 4
                }}
              >
                <Box>
                  <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                    Changed By
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    {log.changed_by_name}
                  </Typography>
                  <Chip
                    label={changedByTypeName(log)}
                    size="small"
                    color={changedByTypeColor(log)}
                    variant="outlined"
                    sx={{ mt: 0.75, height: 22, fontSize: "0.72rem", fontWeight: 600 }}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                    Date & Time
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarToday sx={{ color: "text.disabled", fontSize: 18 }} />
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      {log.created_at
                        ? new Date(log.created_at).toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit"
                        })
                        : "-"}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                    IP Address
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Lan sx={{ color: "text.disabled", fontSize: 18 }} />
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color="text.primary"
                      sx={{ fontFamily: "'Consolas', monospace" }}
                    >
                      {log.ip_address}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
                >
                  <Notes sx={{ color: "text.disabled" }} />
                  Description
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "background.neutral",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    typography: "body2",
                    color: "text.secondary",
                    lineHeight: 1.6
                  }}
                >
                  {log.description}
                </Box>
              </Box>

              {log.delete_reason && (
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
                  >
                    <Notes sx={{ color: "text.disabled" }} />
                    Delete Reason
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "background.neutral",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      typography: "body2",
                      color: "text.secondary",
                      lineHeight: 1.6
                    }}
                  >
                    {log.delete_reason}
                  </Box>
                </Box>)}

              {moduleType >= 1 && moduleType <= 5 && (
                <ModuleDataCard moduleType={moduleType} oldData={oldData} newData={newData} />
              )}

              {diffFields.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                  >
                    <Difference sx={{ color: "text.disabled" }} />
                    Data Changes
                  </Typography>
                  <TableContainer
                    component={Paper}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      overflow: "hidden",
                      boxShadow: "none"
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "background.neutral" }}>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              typography: "caption",
                              letterSpacing: "0.05em",
                              textTransform: "uppercase",
                              color: "text.secondary",
                              width: "25%",
                              borderColor: "divider"
                            }}
                          >
                            Field
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              typography: "caption",
                              letterSpacing: "0.05em",
                              textTransform: "uppercase",
                              color: "text.secondary",
                              borderColor: "divider"
                            }}
                          >
                            Old Value
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              typography: "caption",
                              letterSpacing: "0.05em",
                              textTransform: "uppercase",
                              color: "text.secondary",
                              borderColor: "divider"
                            }}
                          >
                            New Value
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {diffFields.map((field) => (
                          <TableRow key={field} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                typography: "body2",
                                color: "text.secondary",
                                bgcolor: "background.neutral",
                                borderColor: "divider",
                                fontFamily: "'Public Sans', sans-serif"
                              }}
                            >
                              {field}
                            </TableCell>
                            <TableCell
                              sx={{
                                bgcolor: "error.lighter",
                                typography: "body2",
                                fontFamily: "'Consolas', monospace",
                                color: "error.main",
                                borderColor: "divider"
                              }}
                            >
                              {formatValue(oldData[field])}
                            </TableCell>
                            <TableCell
                              sx={{
                                bgcolor: "success.lighter",
                                typography: "body2",
                                fontFamily: "'Consolas', monospace",
                                color: "success.main",
                                borderColor: "divider"
                              }}
                            >
                              {formatValue(newData[field])}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </>
          )}

          <Box sx={{ pt: 3, borderTop: 1, borderColor: "divider", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button
              variant="outlined"
              startIcon={<CodeIcon />}
              color="inherit"
              size="small"
              onClick={() => setShowRawJson((prev) => !prev)}
            >
              {showRawJson ? "Show Card View" : "Show Raw JSON"}
            </Button>
            {/* <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" startIcon={<DownloadIcon />} color="inherit" size="small">
                Export
              </Button>
              <Button variant="contained" size="small">
                Acknowledge Log
              </Button>
            </Box> */}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}

export default function SystemLog() {
  const theme = useTheme();

  const [modules, setModules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [selectedLog, setSelectedLog] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const [filters, setFilters] = useState({
    module_name: "",
    action_type: "",
    source_table: "",
    chpt_id: 0,
    changed_by: "",
    record_id: "",
    from_date: "",
    to_date: ""
  });

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const loadModules = async () => {
      const resp = await fetchSystemLogModules();
      if (resp?.status === 1 && resp?.data) {
        setModules(resp.data);
      }
    };
    loadModules();
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    const params = { page, limit };
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params[key] = value;
    });
    if (debouncedSearch) params.search = debouncedSearch;
    const resp = await fetchSystemLogs(params);
    if (resp?.status === 1 && resp?.data) {
      setLogs(resp.data.logs || []);
      setTotal(resp.data.total || 0);
    }
    setLoading(false);
  }, [page, limit, filters, debouncedSearch]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleReset = () => {
    setFilters({
      module_name: "",
      action_type: "",
      source_table: "",
      chpt_id: 0,
      changed_by: "",
      record_id: "",
      from_date: "",
      to_date: ""
    });
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
  };

  const handleRowClick = (log) => {
    setSelectedLog(log);
    setPanelOpen(true);
  };

  const totalPages = Math.ceil(total / limit);

  const columns = [
    {
      name: "Date",
      selector: (row) => moment(row.created_at).format("DD-MM-YYYY hh:mm A"),
      width: "180px",
      style: { color: theme.palette.text.secondary }
    },
    {
      name: "Module",
      cell: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {row.module_name}
        </Typography>
      ),
      center: true,
      minWidth: "100px"
    },
    {
      name: "Action",
      cell: (row) => <ActionBadge action={row.action_type} />,
      center: true,
      minWidth: "130px"
    },
    {
      name: "Record",
      cell: (row) => (
        <Typography
          variant="body2"
          sx={{
            color: "primary.main",
            fontWeight: 500,
            "&:hover": { textDecoration: "underline" }
          }}
        >
          {row.record_identifier}
        </Typography>
      ),
      center: true,
      minWidth: "140px"
    },
    {
      name: "Changed By",
      selector: (row) => row.changed_by_name,
      width: "200px",
      center: true,
      style: { fontWeight: 500 }
    },
    {
      name: "Description",
      selector: (row) => row.description,
      minWidth: "250px",
      center: true,
      wrap: true,
      style: {
        color: theme.palette.text.secondary,
        maxWidth: "250px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }
    }
  ];

  const primaryColor = theme.palette.primary.main;

  return (
    <Page title="Deleted Records">
      <Container maxWidth={false} sx={{ px: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, mt: 2 }}>
          <Typography variant="h4">
            Deleted Records
          </Typography>
        </Stack>

        <Card
          sx={{
            p: 2,
            mb: 3,
            boxShadow: (t) => t.customShadows?.z1
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              alignItems: "flex-end"
            }}
          >
            <Box sx={{ minWidth: 200 }}>
              <label style={{ display: "block", fontSize: 14, color: "#212B36" }}>Module</label>
              <Select
                placeholder="All Modules"
                isClearable
                value={
                  filters.module_name
                    ? { value: filters.module_name, label: filters.module_name }
                    : null
                }
                onChange={(option) => handleFilterChange("module_name", option ? option.value : "")}
                options={modules.map((m) => ({ value: m.module_name, label: m.module_name }))}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, fontSize: 14 }),
                  menu: (base) => ({ ...base, fontSize: 14 }),
                  placeholder: (base) => ({ ...base, fontSize: 14, color: "#212B36" })
                }}
                menuPortalTarget={document.body}
              />
            </Box>

            <Box sx={{ minWidth: 200 }}>
              <ChapterSelectDropDown
                dropDownLabel="Chapter"
                selectedState={filters}
                chapterKey="chpt_id"
                handleChange={(e) => handleFilterChange("chpt_id", e.value)}
                defaultOptionSelectAll={true}
              />
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From Date"
                value={filters.from_date ? moment(filters.from_date, "YYYY-MM-DD").toDate() : null}
                onChange={(newDate) => handleFilterChange("from_date", newDate ? moment(newDate).format("YYYY-MM-DD") : "")}
                inputFormat="dd/MM/yyyy"
                renderInput={(params) => <TextField {...params} size="small" sx={{ minWidth: 140 }} />}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="To Date"
                value={filters.to_date ? moment(filters.to_date, "YYYY-MM-DD").toDate() : null}
                onChange={(newDate) => handleFilterChange("to_date", newDate ? moment(newDate).format("YYYY-MM-DD") : "")}
                inputFormat="dd/MM/yyyy"
                renderInput={(params) => <TextField {...params} size="small" sx={{ minWidth: 140 }} />}
              />
            </LocalizationProvider>

            <TextField
              placeholder="Search by Record ID, Change By, or Description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="disabled" />
                  </InputAdornment>
                )
              }}
              sx={{ width: { xs: "100%", sm: 280 } }}
            />

            <Button variant="contained" onClick={() => { setDebouncedSearch(search); setPage(1); }} sx={{ height: 40 }}>
              Apply Filters
            </Button>

            <Button variant="outlined" onClick={handleReset} color="inherit" sx={{ height: 40 }}>
              Reset
            </Button>


          </Box>
        </Card>

        <Card
          sx={{
            boxShadow: (t) => t.customShadows?.z1,
            overflow: "hidden"
          }}
        >
          <DataTable
            columns={columns}
            data={logs}
            progressPending={loading}
            progressComponent={<CircularProgress sx={{ my: 4 }} />}
            onRowClicked={handleRowClick}
            customStyles={{
              headRow: {
                style: {
                  backgroundColor: theme.palette.background.neutral,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  minHeight: "44px"
                }
              },
              headCells: {
                style: {
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  color: theme.palette.text.secondary,
                  textTransform: "uppercase",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  paddingTop: "12px",
                  paddingBottom: "12px"
                }
              },
              rows: {
                style: {
                  fontSize: "14px",
                  fontWeight: 400,
                  color: theme.palette.text.primary,
                  minHeight: "48px",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                    cursor: "pointer"
                  }
                }
              },
              cells: {
                style: {
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  paddingTop: "12px",
                  paddingBottom: "12px"
                }
              }
            }}
            noHeader
            highlightOnHover={false}
            pointerOnHover
            responsive
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "background.neutral"
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} entries
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                size="small"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  color: "text.secondary",
                  width: 32,
                  height: 32,
                  "&.Mui-disabled": { opacity: 0.5 }
                }}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    size="small"
                    sx={{
                      minWidth: 32,
                      height: 32,
                      p: 0,
                      border: 1,
                      borderColor: page === pageNum ? "primary.main" : "divider",
                      borderRadius: 1,
                      bgcolor: page === pageNum ? "primary.main" : "background.paper",
                      color: page === pageNum ? "primary.contrastText" : "text.secondary",
                      fontWeight: 500,
                      fontSize: "12px",
                      "&:hover": {
                        bgcolor: page === pageNum ? "primary.main" : "action.hover"
                      }
                    }}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <IconButton
                size="small"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  color: "text.secondary",
                  width: 32,
                  height: 32,
                  "&.Mui-disabled": { opacity: 0.5 }
                }}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Card>
      </Container>

      <LogDetailPanel
        open={panelOpen}
        log={selectedLog}
        onClose={() => setPanelOpen(false)}
      />
    </Page>
  );
}
